import {useRouter} from 'next/router';
import type React from 'react';
import type {RecruitmentsType} from '../../../../core/entities/companyDetail/schema';
import {convertDeadlineRecruitment} from '../../../../core/factories/recruitment/convertDeadlineRecruitment';
import {getSheduleEnd} from '../../../../core/factories/recruitment/getScheduleEnd';
import {useGetRecruitments} from '../../../../core/repositories/companyDetailRepository';
import {sortRecruitmentsByDeadline} from '../../usecase/sortRecruitmentsByDeadline';
import {
  CompanyFollowCompletionModal,
  type CompletionRecruitment,
} from './Presenter/CompanyFollowCompletionModal';

type Props = {
  companyId: number;
  companyName: string;
  opened: boolean;
  onClose: () => void;
  checked: boolean;
  setChecked: (value: boolean) => void;
  recruitmentLinkHref?: string;
};

export const CompanyFollowCompletionModalContainer: React.FC<Props> = ({
  companyId,
  companyName,
  opened,
  onClose,
  checked,
  setChecked,
  recruitmentLinkHref = `/company/${companyId}`,
}) => {
  const {data} = useGetRecruitments({
    companyId: String(companyId),
    config: {enabled: opened},
  });

  const recruitments = buildRecruitments(
    data?.ok ? data.value.recruitments : [],
  );

  const {asPath} = useRouter();

  return (
    <CompanyFollowCompletionModal
      opened={opened}
      onClose={onClose}
      checked={checked}
      setChecked={setChecked}
      companyId={companyId}
      companyName={companyName}
      recruitmentLinkHref={recruitmentLinkHref}
      recruitments={recruitments}
      pagePath={asPath}
    />
  );
};

const MAX_DISPLAY_COUNT = 2;

const buildDeadline = (
  schedules: RecruitmentsType[number]['schedules'],
  now: Date,
): {deadlineText: string; deadlineColor: string} => {
  const {scheduleEnd} = getSheduleEnd(schedules, now);
  if (!scheduleEnd) {
    return {deadlineText: '', deadlineColor: ''};
  }

  const {deadline, deadlineColor} = convertDeadlineRecruitment(scheduleEnd);
  return {deadlineText: deadline, deadlineColor};
};

const buildRecruitments = (
  recruitments: RecruitmentsType,
): CompletionRecruitment[] => {
  const now = new Date();
  return sortRecruitmentsByDeadline(recruitments, now)
    .slice(0, MAX_DISPLAY_COUNT)
    .flatMap((recruitment) => {
      const schedule = recruitment.schedules[0];
      const company = recruitment.companies[0];
      if (!schedule || !company) {
        return [];
      }

      const {deadlineText, deadlineColor} = buildDeadline(
        recruitment.schedules,
        now,
      );

      return [
        {
          id: recruitment.id,
          title: recruitment.title,
          detailPageURL: recruitment.detailPageURL,
          deadlineText,
          deadlineColor,
          schedule: {
            id: schedule.id,
            isCalendarRegistered: schedule.isCalendarRegistered,
            calendarRegisteredCount: schedule.calendarRegisteredCount,
          },
          company: {
            id: company.id,
            name: company.name,
          },
        },
      ];
    });
};
